import { useState, useId, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import { useSession } from '@/store/useSession'
import styles from './ClientLoginPage.module.css'

const CLIENT_USERNAME = 'client'
const CLIENT_PASSWORD = 'FitU2026!'

function isValidClientLogin(username: string, password: string): boolean {
  return (
    username.trim().toLowerCase() === CLIENT_USERNAME &&
    password === CLIENT_PASSWORD
  )
}

export default function ClientLoginPage() {
  const { palette } = useTheme()
  const navigate = useNavigate()
  const { setClientName, setUserRole } = useSession()
  const nameId = useId()
  const passwordId = useId()

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setBusy(true)

    await new Promise(r => setTimeout(r, 400))

    if (isValidClientLogin(name, password)) {
      setClientName(name.trim())
      setUserRole('client')
      navigate('/client-home')
      return
    }

    setError("We didn't recognise those details. Try again.")
    setBusy(false)
  }

  const paletteVars = {
    '--cl-bg':   palette.bg,
    '--cl-text': palette.text,
    '--cl-ring': palette.ring,
  } as React.CSSProperties

  const canSubmit = name.trim().length > 0 && password.length > 0

  return (
    <main className={styles.page} style={paletteVars} aria-label="Client sign in">
      <div className={styles.inner}>
        <header className={styles.header}>
          <h1 className={styles.heading}>Welcome back</h1>
          <p className={styles.subtitle}>Sign in to your space.</p>
        </header>

        <form
          className={styles.form}
          onSubmit={handleSubmit}
          noValidate
          aria-label="Client sign in form"
        >
          <div className={styles.field}>
            <label htmlFor={nameId} className={styles.label}>
              Name
            </label>
            <input
              id={nameId}
              type="text"
              className={styles.input}
              value={name}
              onChange={e => { setName(e.target.value); setError('') }}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              aria-required="true"
              aria-describedby={error ? 'client-login-error' : undefined}
              placeholder="Your name"
            />
          </div>

          <div className={styles.field}>
            <label htmlFor={passwordId} className={styles.label}>
              Password
            </label>
            <input
              id={passwordId}
              type="password"
              className={styles.input}
              value={password}
              onChange={e => { setPassword(e.target.value); setError('') }}
              autoComplete="current-password"
              required
              aria-required="true"
              aria-describedby={error ? 'client-login-error' : undefined}
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p id="client-login-error" className={styles.errorMsg} role="alert" aria-live="polite">
              {error}
            </p>
          )}

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={busy || !canSubmit}
            aria-disabled={busy || !canSubmit}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <button type="button" className={styles.backLink} onClick={() => navigate('/')}>
          ← Back
        </button>
      </div>
    </main>
  )
}
